// =============================================================
// notebook.ts — client rules for the Notes app, shared by web and iOS.
//
// The list groups the way iOS Notes does: Pinned first, then Today,
// Yesterday, Previous 7 Days, Previous 30 Days, then one section per month
// ("September 2026"). Grouping is by the note's LOCAL modified day, computed
// from the device clock — a note edited at 11pm and one at 1am the next
// morning belong to different days wherever the reader is.
// =============================================================

export type NotebookLinkType = "Deal" | "DealDetail" | "Task";

export interface NotebookLink {
  type: NotebookLinkType;
  id: string;
  label?: string | null;
  dealName?: string | null;
  detailType?: string | null;
  dealId?: string | null;
}

export interface NotebookSummary {
  id: string;
  title: string;
  snippet: string;
  modifiedUTC: string;
  createdUTC?: string;
  deletedUTC?: string | null;
  pinned: boolean;
  isOwner: boolean;
  isPrivate: boolean;
  links: NotebookLink[];
}

export interface NotebookSection<T> {
  key: string;
  title: string;
  items: T[];
}

const DAY_MS = 86400000;

function localDayStart(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Which section a note modified at `iso` belongs in, relative to `now`. */
export function notebookSectionFor(iso: string | null | undefined, now: Date = new Date()): { key: string; title: string } {
  const t = iso ? new Date(iso) : null;
  if (!t || Number.isNaN(t.getTime())) return { key: "older", title: "Older" };
  const days = Math.round((localDayStart(now) - localDayStart(t)) / DAY_MS);
  if (days <= 0) return { key: "today", title: "Today" };
  if (days === 1) return { key: "yesterday", title: "Yesterday" };
  if (days <= 7) return { key: "week", title: "Previous 7 Days" };
  if (days < 30) return { key: "month", title: "Previous 30 Days" };
  const title = t.toLocaleString("en-US", { month: "long", year: "numeric" });
  return { key: `m-${t.getFullYear()}-${t.getMonth()}`, title };
}

/** Pinned first, then date sections, each newest-first. Order of first appearance is kept. */
export function groupNotebook<T extends { pinned?: boolean; modifiedUTC: string }>(
  notes: T[],
  now: Date = new Date(),
): NotebookSection<T>[] {
  const sorted = [...(notes || [])].sort(
    (a, b) => new Date(b.modifiedUTC).getTime() - new Date(a.modifiedUTC).getTime(),
  );
  const out: NotebookSection<T>[] = [];
  const byKey = new Map<string, NotebookSection<T>>();
  const push = (key: string, title: string, item: T) => {
    let s = byKey.get(key);
    if (!s) {
      s = { key, title, items: [] };
      byKey.set(key, s);
      out.push(s);
    }
    s.items.push(item);
  };
  const pinned = sorted.filter((n) => n.pinned);
  for (const n of pinned) push("pinned", "Pinned", n);
  for (const n of sorted) {
    if (n.pinned) continue;
    const { key, title } = notebookSectionFor(n.modifiedUTC, now);
    push(key, title, n);
  }
  return out;
}

/** Short label for a link chip: "Deal · Genesis Bank", "R/E · Baltz Property", "Task · Estoppel". */
export function notebookLinkLabel(l: NotebookLink): string {
  const name = l.label || "(record)";
  if (l.type === "Deal") return name;
  if (l.type === "DealDetail") return `${l.detailType || "Item"} · ${name}`;
  return `Task · ${name}`;
}

/** The time line on a list row: a clock time today, a weekday this week, else a date. */
export function notebookRowTime(iso: string | null | undefined, now: Date = new Date()): string {
  const t = iso ? new Date(iso) : null;
  if (!t || Number.isNaN(t.getTime())) return "";
  const days = Math.round((localDayStart(now) - localDayStart(t)) / DAY_MS);
  if (days <= 0) return t.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (days === 1) return "Yesterday";
  if (days < 7) return t.toLocaleDateString("en-US", { weekday: "long" });
  return t.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "2-digit" });
}
